if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
/**
 *  @class
 *  @function ThemeHeader
 */

if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = this.querySelector('.mobile-toggle-wrapper');
      this.isStyle4 = this.classList.contains('style4');
      this.lastScrollY = 0;
      this.topRowHeight = 0;

      document.addEventListener('keyup', (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === 'ESCAPE') {
            this.toggle.removeAttribute('open');
            this.toggle.classList.remove('active');
          }
        }
      });
      if (this.classList.contains('header-sticky--active')) {
        document.body.classList.add('header-sticky--active');
      }
      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        if (this.toggle.classList.contains('active')) {
          e.preventDefault();
          document.body.classList.remove('overflow-hidden');
          this.toggle.classList.remove('active');
          this.closeAnimation(this.toggle);
        } else {
          // Re-measure drawer top before opening so there is no gap
          if (this.isStyle4) {
            this.updateMobileDrawerTop();
          }
          document.body.classList.add('overflow-hidden');
          setTimeout(() => {
            this.toggle.classList.add('active');
          });
        }
        window.dispatchEvent(new Event('resize.resize-select'));
      });

      // Sticky Header Class
      window.addEventListener('scroll', this.setStickyClass.bind(this), {
        passive: true
      });

      // Mobile Menu offset
      window.addEventListener('scroll', this.setHeaderOffset.bind(this), {
        passive: true
      });
      window.addEventListener('scroll', this.setHeaderHeight.bind(this), {
        passive: true
      });

      window.dispatchEvent(new Event('scroll'));

      if (document.querySelector('.announcement-bar-section')) {

        window.addEventListener('scroll', this.setAnnouncementHeight(), {
          passive: true
        });
        window.dispatchEvent(new Event('resize'));
      }

      // Buttons.
      this.menu.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));

      // Style4: scroll behavior — hide top row, keep nav bar sticky
      if (this.isStyle4) {
        this.initStyle4();
      }
    }
    initStyle4() {
      const topRow = this.querySelector('.header-style4-top');
      const navBar = this.querySelector('.header-style4-nav');
      if (topRow) {
        this.topRowHeight = topRow.offsetHeight;
        // Publish the exact height as a CSS variable so the
        // negative-margin collapse can use it.
        document.documentElement.style.setProperty('--style4-top-row-h', this.topRowHeight + 'px');
      }
      // Cache nav bar height so we can lock it when top row collapses
      if (navBar) {
        this.navBarHeight = navBar.offsetHeight;
      }
      this._style4Ticking = false;
      this._topHidden = false;
      // Cooldown timestamp – ignore scroll events for a short
      // period after every hide/show toggle to break the feedback loop.
      this._style4Cooldown = 0;

      // Set the mobile drawer top position to match the exact
      // bottom edge of the visible header (top row only on mobile).
      this.updateMobileDrawerTop();
      window.addEventListener('resize', debounce(() => {
        this.updateMobileDrawerTop();
      }, 100));

      // Scroll listener for hiding top row
      window.addEventListener('scroll', this.handleStyle4Scroll.bind(this), { passive: true });

      // Recalculate topRowHeight on resize (debounced)
      window.addEventListener('resize', debounce(() => {
        if (!this._topHidden && topRow) {
          this.topRowHeight = topRow.offsetHeight;
          document.documentElement.style.setProperty('--style4-top-row-h', this.topRowHeight + 'px');
        }
        if (navBar) {
          this.navBarHeight = navBar.offsetHeight;
        }
      }, 200));

      // Embedded search integration with predictive search
      this.initStyle4Search();
    }
    updateMobileDrawerTop() {
      // On mobile the nav bar is hidden, so the visible header is
      // just the top row. Use getBoundingClientRect().bottom which
      // gives the exact pixel position relative to the viewport,
      // accounting for sticky positioning and any scroll offset.
      // This guarantees the drawer sits flush against the header
      // with zero gap.
      const topRow = this.querySelector('.header-style4-top');
      if (topRow) {
        const rect = topRow.getBoundingClientRect();
        const bottomEdge = Math.round(rect.bottom);
        document.documentElement.style.setProperty('--style4-mobile-header-h', bottomEdge + 'px');
      }
    }
    handleStyle4Scroll() {
      if (this._style4Ticking) return;
      this._style4Ticking = true;

      requestAnimationFrame(() => {
        this._style4Ticking = false;

        const now = performance.now();
        // Skip events during the cooldown window after a toggle
        if (now < this._style4Cooldown) return;

        const currentScrollY = window.scrollY;
        const hideThreshold = this.topRowHeight || 85;

        if (!this._topHidden && currentScrollY > hideThreshold) {
          this._topHidden = true;
          this.classList.add('top-hidden');
          // Lock the header height to the nav bar height
          if (this.navBarHeight) {
            document.documentElement.style.setProperty('--header-height', this.navBarHeight + 'px');
          }
          // 300 ms cooldown – ignore scroll events caused by the
          // layout shift of collapsing the top row
          this._style4Cooldown = now + 300;
        } else if (this._topHidden && currentScrollY <= 5) {
          // Only show the top row again when the user scrolls
          // (almost) all the way back to the top
          this._topHidden = false;
          this.classList.remove('top-hidden');
          document.documentElement.style.setProperty('--header-height', this.clientHeight + 'px');
          this._style4Cooldown = now + 300;
        }

        this.lastScrollY = currentScrollY;
      });
    }
    initStyle4Search() {
      const searchInput = this.querySelector('#header-style4-search-input');
      const searchForm = this.querySelector('#header-style4-search-form');
      const dropdown = this.querySelector('#style4-search-dropdown');
      const resultsContainer = dropdown ? dropdown.querySelector('.style4-search-dropdown__results') : null;

      if (!searchInput || !dropdown || !resultsContainer) return;

      let abortController = null;
      let isOpen = false;

      const openDropdown = () => {
        if (!isOpen) {
          dropdown.style.display = '';
          dropdown.classList.add('is-open');
          isOpen = true;
        }
      };

      const closeDropdown = () => {
        if (isOpen) {
          dropdown.classList.remove('is-open');
          dropdown.classList.remove('is-loading');
          isOpen = false;
        }
      };

      const fetchResults = (term) => {
        if (abortController) abortController.abort();
        abortController = new AbortController();

        dropdown.classList.add('is-loading');
        openDropdown();

        fetch(
          `${theme.routes.predictive_search_url}?q=${encodeURIComponent(term)}&${encodeURIComponent('resources[type]')}=product,article,query,page&${encodeURIComponent('resources[limit]')}=10&resources[options][fields]=title,product_type,vendor,variants.title,variants.sku&section_id=predictive-search`,
          { signal: abortController.signal }
        )
          .then((r) => {
            if (!r.ok) throw new Error(r.status);
            return r.text();
          })
          .then((text) => {
            const markup = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('#shopify-section-predictive-search');
            if (markup) {
              resultsContainer.innerHTML = markup.innerHTML;
              // Activate first category tab and results
              const firstTab = resultsContainer.querySelector('.search-categories a');
              if (firstTab) firstTab.classList.add('active');
              const firstSection = resultsContainer.querySelector('.search-results');
              if (firstSection) firstSection.classList.add('active');
              // Bind category tabs
              resultsContainer.querySelectorAll('.search-categories a').forEach((tab) => {
                tab.addEventListener('click', (e) => {
                  e.preventDefault();
                  resultsContainer.querySelectorAll('.search-categories a').forEach((t) => t.classList.remove('active'));
                  tab.classList.add('active');
                  resultsContainer.querySelectorAll('.search-results').forEach((s) => s.classList.remove('active'));
                  const target = resultsContainer.querySelector(tab.getAttribute('href'));
                  if (target) target.classList.add('active');
                });
              });
            }
            dropdown.classList.remove('is-loading');
          })
          .catch((err) => {
            if (err.name !== 'AbortError') {
              dropdown.classList.remove('is-loading');
            }
          });
      };

      // Input handler
      searchInput.addEventListener('input', debounce(() => {
        const term = searchInput.value.trim();
        if (term.length < 2) {
          resultsContainer.innerHTML = '';
          closeDropdown();
          return;
        }
        fetchResults(term);
      }, 300));

      // Focus: reopen if there's a query
      searchInput.addEventListener('focus', () => {
        const term = searchInput.value.trim();
        if (term.length >= 2 && resultsContainer.innerHTML.trim()) {
          openDropdown();
        }
      });

      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.header-style4-search')) {
          closeDropdown();
        }
      });

      // Close on Escape
      searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          closeDropdown();
          searchInput.blur();
        }
      });

      // Form submit
      searchForm.addEventListener('submit', (e) => {
        if (searchInput.value.trim() === '') {
          e.preventDefault();
          searchInput.focus();
        } else {
          closeDropdown();
        }
      });
    }
    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        let h = this.getBoundingClientRect().top;
        if (h <= 0) {
          this.classList.add('is-sticky');
        } else {
          this.classList.remove('is-sticky');
        }
      }
    }
    setAnnouncementHeight() {
      const a_bar = document.querySelector('.announcement-bar-section');
      let h = a_bar.clientHeight;
      document.documentElement.style.setProperty('--announcement-height', h + 'px');
    }
    setHeaderOffset() {
      let h = this.header_section.getBoundingClientRect().top;
      document.documentElement.style.setProperty('--header-offset', h + 'px');
    }
    setHeaderHeight() {
      // When style4 top row is hidden, handleStyle4Scroll manages
      // --header-height directly to avoid layout-shift feedback loops.
      if (this._topHidden) return;
      let h = this.clientHeight;
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }
    onSummaryClick(event) {
      // Başlık linkine tıklandıysa navigate et, toggle yapma
      if (event.target.closest('a.parent-link-title')) {
        event.stopPropagation();
        return;
      }
      event.preventDefault();
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const isOpen = detailsElement.hasAttribute('open');
      if (isOpen) {
        detailsElement.classList.remove('submenu-open');
        this.closeAnimation(detailsElement);
      } else {
        detailsElement.setAttribute('open', '');
        detailsElement.classList.add('submenu-open');
      }
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('submenu-open');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}

/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();

      this.submenus = this.querySelectorAll('.thb-full-menu .menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');
    }
    connectedCallback() {
      const _this = this;

      // Mega-menu overflow protection: reposition on hover
      this.querySelectorAll('.thb-full-menu > li.menu-item-has-megamenu').forEach(function(li) {
        li.addEventListener('mouseenter', function() {
          _this.repositionMegaMenu(li);
        });
      });

      // Also reposition on resize
      window.addEventListener('resize', debounce(function () {
        _this.querySelectorAll('.thb-full-menu > li.menu-item-has-megamenu').forEach(function(li) {
          _this.repositionMegaMenu(li);
        });
      }, 100));

      if (!this.submenus.length) {
        return;
      }
      // resize on initial load
      document.fonts.ready.then(function () {
        window.addEventListener('resize', debounce(function () {
          _this.resizeSubMenus();
        }, 100));
        _this.resizeSubMenus();
      });

    }
    /**
     * Reposition mega-menu-container so it never overflows the viewport.
     * Default CSS: left:50%; transform:translateX(-50%) — centered under parent.
     * This method adjusts left/transform when the dropdown would overflow left or right.
     */
    repositionMegaMenu(li) {
      var container = li.querySelector('.mega-menu-container');
      if (!container) return;

      var isStyle3 = li.classList.contains('mega-style3');

      // Reset to default CSS positioning
      container.style.left = '';
      container.style.right = '';
      container.style.transform = '';

      // Force layout so we can measure
      var liRect = li.getBoundingClientRect();
      var containerWidth = container.offsetWidth;
      var vpWidth = window.innerWidth;
      var margin = 10; // minimum gap from viewport edges

      if (isStyle3) {
        // Style 3: left-aligned under parent by CSS (left:0)
        var leftEdge = liRect.left;
        var rightEdge = leftEdge + containerWidth;
        if (rightEdge > vpWidth - margin) {
          // Overflow right — shift left
          var shift = rightEdge - (vpWidth - margin);
          container.style.left = -shift + 'px';
        }
        if (liRect.left - (rightEdge > vpWidth - margin ? (rightEdge - (vpWidth - margin)) : 0) < margin) {
          container.style.left = (margin - liRect.left) + 'px';
        }
      } else {
        // Style 1 & 2: centered under parent (left:50%; transform:translateX(-50%))
        var liCenterX = liRect.left + liRect.width / 2;
        var idealLeft = liCenterX - containerWidth / 2;
        var idealRight = idealLeft + containerWidth;

        if (idealLeft < margin) {
          // Would overflow left — align to left edge of viewport with margin
          var offsetLeft = margin - liRect.left;
          container.style.left = offsetLeft + 'px';
          container.style.transform = 'none';
        } else if (idealRight > vpWidth - margin) {
          // Would overflow right — shift left
          var overflowRight = idealRight - (vpWidth - margin);
          var offsetLeft2 = liCenterX - containerWidth / 2 - liRect.left - overflowRight;
          container.style.left = offsetLeft2 + 'px';
          container.style.transform = 'none';
        }
      }
    }
    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.offsetWidth,
            total = w + l;

          if (total > window.innerWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);

  /* Style 2 Mega Menu – collection image hover switching */
  function initMegaMenuStyle2() {
    document.querySelectorAll('.mega-menu--style2').forEach(function(megaMenu) {
      var collectionLinks = megaMenu.querySelectorAll('.mega-menu-collection-link');
      var previewImages = megaMenu.querySelectorAll('.mega-menu-preview-img');

      if (!collectionLinks.length || !previewImages.length) return;

      collectionLinks.forEach(function(link) {
        link.addEventListener('mouseenter', function() {
          var idx = this.getAttribute('data-collection-index');

          // Remove active from all images
          previewImages.forEach(function(img) {
            img.classList.remove('is-active');
          });

          // Activate matching image
          var target = megaMenu.querySelector('.mega-menu-preview-img[data-collection-index="' + idx + '"]');
          if (target) {
            target.classList.add('is-active');
          }
        });
      });

      // On mouse leave from collection list, revert to first image
      var colList = megaMenu.querySelector('.mega-menu-col--collections');
      if (colList) {
        colList.addEventListener('mouseleave', function() {
          previewImages.forEach(function(img) {
            img.classList.remove('is-active');
          });
          if (previewImages[0]) {
            previewImages[0].classList.add('is-active');
          }
        });
      }
    });
  }

  // Run after DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMegaMenuStyle2);
  } else {
    initMegaMenuStyle2();
  }

  // Re-init in Shopify theme editor
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', initMegaMenuStyle2);
  }
}